import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Link,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contacto - FinMaple',
  description: 'Contacta al equipo de FinMaple para preguntas sobre finanzas en Canadá.',
  url: 'https://www.finmaple.com/contacto',
  mainEntity: {
    '@type': 'Organization',
    name: 'FinMaple',
    email: 'hola@finmaple.com',
    url: 'https://www.finmaple.com',
    sameAs: ['https://www.linkedin.com/in/alvizcarlos/'],
  },
};

const ContactUsPage = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormErrors({ ...formErrors, [e.target.name]: '' });
  };

  const validateForm = () => {
    let errors = {};
    if (!formData.name.trim()) errors.name = t('pages.contacto.errorName');
    if (!formData.email.trim()) errors.email = t('pages.contacto.errorEmail');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('pages.contacto.errorEmailInvalid');
    if (!formData.subject.trim()) errors.subject = t('pages.contacto.errorSubject');
    if (!formData.message.trim()) errors.message = t('pages.contacto.errorMessage');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `${t('pages.contacto.nameLabel')}: ${formData.name}\n${t('pages.contacto.emailLabel')}: ${formData.email}\n\n${formData.message}`
      );
      window.location.href = `mailto:hola@finmaple.com?subject=${subject}&body=${body}`;
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }
  };

  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="contact"
        title={t('pages.contacto.seoTitle')}
        description={t('pages.contacto.seoDescription')}
        canonical="/contacto"
        routeKey="contact"
        schema={contactSchema}
        noIndex={i18n.language !== 'es'}
      />
      <PageHeader
        variant="functional"
        title={t('pages.contacto.title')}
        subtitle={t('pages.contacto.subtitle')}
        breadcrumbs={[{ label: t('pages.contacto.breadcrumb') }]}
      />
      <Container maxWidth="md" sx={{ py: 5 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, color: '#1E3A5F' }}>
            {t('pages.contacto.getInTouch')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('pages.contacto.getInTouchDescription')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmailIcon sx={{ color: '#1E3A5F', mr: 1 }} />
            <Link href="mailto:hola@finmaple.com" underline="hover">
              hola@finmaple.com
            </Link>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccessTimeIcon sx={{ color: '#1E3A5F', mr: 1 }} />
            <Typography variant="body1">
              {t('pages.contacto.responseTime')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LinkedInIcon sx={{ color: '#1E3A5F', mr: 1 }} />
            <Link href="https://www.linkedin.com/in/alvizcarlos/" target="_blank" rel="noopener noreferrer" underline="hover">
              {t('pages.contacto.linkedinLabel')}
            </Link>
          </Box>
          <Box sx={{ bgcolor: '#EEF2F6', borderRadius: '10px', p: 2.5, mt: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>FinMaple</strong> {t('pages.contacto.aboutBox')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {submitSuccess && (
            <Alert severity="success" onClose={() => setSubmitSuccess(false)} sx={{ mb: 2 }}>
              {t('pages.contacto.successMessage')}
            </Alert>
          )}
          <form onSubmit={handleSubmit} noValidate>
            <TextField
              label={t('pages.contacto.nameLabel')} name="name" fullWidth required margin="normal"
              value={formData.name} onChange={handleChange}
              error={!!formErrors.name} helperText={formErrors.name}
            />
            <TextField
              label={t('pages.contacto.emailLabel')} name="email" fullWidth required margin="normal"
              value={formData.email} onChange={handleChange}
              error={!!formErrors.email} helperText={formErrors.email}
            />
            <TextField
              label={t('pages.contacto.subjectLabel')} name="subject" fullWidth required margin="normal"
              value={formData.subject} onChange={handleChange}
              error={!!formErrors.subject} helperText={formErrors.subject}
            />
            <TextField
              label={t('pages.contacto.messageLabel')} name="message" fullWidth required multiline rows={4} margin="normal"
              value={formData.message} onChange={handleChange}
              error={!!formErrors.message} helperText={formErrors.message}
            />
            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon />}
              sx={{ mt: 2, borderRadius: '14px', backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#1A3152' } }}
            >
              {t('pages.contacto.submitButton')}
            </Button>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 1, textAlign: 'center' }}>
              {t('pages.contacto.submitHint')}
            </Typography>
          </form>
        </Grid>
      </Grid>
    </Container>
    </Box>
  );
};

export default ContactUsPage;
